import { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Indicador deslizante de um segmented control.
 *
 * Mede o item `[data-pill-active="true"]` e anima `left`/`width` no CSS
 * (`.dream-pill`). Sem o `ready`, o primeiro frame pintaria a pílula em 0,0.
 */

export interface SlidingPill {
  left: number;
  width: number;
  top: number;
  height: number;
  ready: boolean;
}

export function useSlidingPill(activeKey: string): {
  listRef: React.RefObject<HTMLDivElement | null>;
  pill: SlidingPill;
} {
  const listRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<SlidingPill>({
    left: 0,
    width: 0,
    top: 0,
    height: 0,
    ready: false,
  });

  const update = useCallback(() => {
    const list = listRef.current;
    if (list === null) return;
    const selected = list.querySelector('[data-pill-active="true"]');
    if (!(selected instanceof HTMLElement)) return;
    setPill({
      left: selected.offsetLeft,
      width: selected.offsetWidth,
      top: selected.offsetTop,
      height: selected.offsetHeight,
      ready: true,
    });
  }, []);

  useLayoutEffect(() => {
    void activeKey;
    update();
    const list = listRef.current;
    if (list === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(list);
    for (const child of list.children) {
      if (child instanceof HTMLElement) observer.observe(child);
    }
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [activeKey, update]);

  return { listRef, pill };
}
